import type { ProductTypeEnum } from '@src/types/types';
import { getProductGroupLabel, getProductType, productTypeLabel, type ProductGroup } from '@src/utils';
import type { ValueFormatterParams } from 'ag-grid-community';
import { get } from 'lodash-es';
import { baseColumn } from './baseColumn';
import type { ColDefFactory, Column } from './types';

export interface ProductTypeColumnParams {
  /**
   * When true, reads `security.ProductType` and formats with `productTypeLabel`.
   * Default false uses `getProductType()` and `getProductGroupLabel()` for blotter display semantics.
   */
  useSecurityProductType?: boolean;
}

/** Grabs the product type off of the the security associated with your data. The security is retrieved using data.Symbol */
export const productType: ColDefFactory<Column<ProductTypeColumnParams>> = column => {
  const useSecurityProductType = column.params?.useSecurityProductType ?? false;

  return {
    ...baseColumn(column),
    valueGetter: ({ data, context }) => {
      if (data == null) {
        return '';
      }

      const symbol = column.field ? get(data, column.field) : data.Symbol;
      if (!symbol) {
        return '';
      }

      const security = context.current.securitiesBySymbol?.get(symbol);
      if (useSecurityProductType) {
        return security?.ProductType ?? '';
      }

      return getProductType(security);
    },
    valueFormatter: ({ value }: ValueFormatterParams<unknown, ProductGroup | ProductTypeEnum>) => {
      if (value == null) {
        return '';
      }

      if (useSecurityProductType) {
        return productTypeLabel(value as ProductTypeEnum);
      }

      return getProductGroupLabel(value as ProductGroup);
    },
  };
};
